import type { Locale } from 'date-fns';
import { format as formatDateLib, formatRelative } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { canUseDOM } from 'exenv';

import { relativeLocale as svRelativeLocale, svLocale } from './dateLocale';

// Set timezone when server rendering.
//
// We'll optimize for where most of our visitors are located. By default this is
// set to 'Europe/Stockholm' which is the timezone for Sweden but it can be
// overridden by the consumer of this lib.
// For the few located in another timezone the server rendered date will be
// incorrect but when the client re-renders they will get a correct date string in
// their timezone.
function getZonedDate(date: Date, timezone = 'Europe/Stockholm'): Date {
  if (canUseDOM && process.env.NODE_ENV !== 'test') {
    return date;
  }

  let tz = timezone;

  if (
    typeof Intl.supportedValuesOf === 'function' &&
    !Intl.supportedValuesOf('timeZone').includes(timezone)
  ) {
    tz = 'Europe/Stockholm';
    console.warn(
      `Invalid timezone "${timezone}" provided. Falling back to default timezone "${tz}".`,
    );
  }

  return toZonedTime(date.toISOString(), tz);
}

/**
 * Format a Date into a string.
 *
 * Formatting docs: https://date-fns.org/docs/format
 */
export function formatDate(
  date: Date,
  format = 'yyyy-MM-dd',
  locale: Locale = svLocale,
  timezone?: string,
): string {
  return formatDateLib(getZonedDate(date, timezone), format, {
    locale,
  }).replace(/\.$/, ''); // Remove possible trailing dot from shortened month
}

/**
 * Format a Date into words relative to now.
 *
 * Docs: https://date-fns.org/docs/formatRelative
 */
export function formatDateRelative(
  date: Date,
  relativeTo: Date = new Date(),
  locale: Locale = svRelativeLocale,
  timezone?: string,
): string {
  return formatRelative(
    getZonedDate(date, timezone),
    getZonedDate(relativeTo, timezone),
    {
      locale,
    },
  ).replace(/\.$/, ''); // Remove possible trailing dot from shortened month
}
